// src/hooks/useIssueOrdering.js
/* global BigInt */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/**
 * Stable dense ordering for Jira issues using BigInt ranks.
 * Ranks are serialized to strings for network/storage.
 *
 * Assumptions:
 * - `issues` prop is already in your desired *default* order from the API (deadline-sorted).
 * - You pass in any server-saved ranks (per user/view) via `initialRanks`.
 * - You tell us which "group" an issue belongs to (e.g., pinned vs unpinned, or Active/Done),
 *   so we never compute ranks across groups.
 */

const BASE_STEP = 2n ** 32n; // Huge gap to allow many inserts between neighbors
const START     = BASE_STEP; // First assigned rank in a group

const toBig = (v) => (typeof v === 'bigint' ? v : window.BigInt(v));
const toStr = (v) => v.toString();

/** Compute a rank strictly between prev and next if both exist, else extend at ends. */
function between(prevRankStr, nextRankStr) {
  const hasPrev = prevRankStr != null;
  const hasNext = nextRankStr != null;

  if (hasPrev && hasNext) {
    const prev = toBig(prevRankStr);
    const next = toBig(nextRankStr);
    const diff = next - prev;
    if (diff > 1n) return toStr(prev + diff / 2n); // midpoint
    return null; // no room → needs a rebalance
  }
  if (hasPrev)  return toStr(toBig(prevRankStr) + BASE_STEP); // after last
  if (hasNext) {
    const newRank = toBig(nextRankStr) - BASE_STEP;
    // If rank would be 0 or negative, signal rebalance needed
    if (newRank <= 0n) return null;
    return toStr(newRank); // before first
  }
  return toStr(START); // empty group case
}

/** Re-number a whole group's keys sequentially with large gaps, preserving order. */
function renumberGroup(keys) {
  const newRanks = {};
  let r = START;
  for (const k of keys) {
    newRanks[k] = toStr(r);
    r += BASE_STEP;
  }
  return newRanks;
}

/**
 * Fill missing ranks in a single group using the group's *default* order (API order),
 * respecting any existing anchor ranks. If there's space between anchors, we distribute.
 * If a span is too tight, we fall back to re-number the entire group.
 */
function fillMissingRanks(defaultOrderedKeys, ranksMap) {
  const haveRank = (k) => ranksMap[k] != null;
  const anchors = defaultOrderedKeys.filter(haveRank);

  // If no anchors at all → simple full renumber
  if (anchors.length === 0) {
    return renumberGroup(defaultOrderedKeys);
  }

  // Start from a copy; we'll patch only missing keys when there's room
  const updates = {};

  const getRank = (k) => toBig(ranksMap[k]);

  // Work a run that lies between prevAnchor and nextAnchor in default order
  const assignRun = (runKeys, prevAnchorKey, nextAnchorKey) => {
    if (runKeys.length === 0) return;

    const hasPrev = !!prevAnchorKey;
    const hasNext = !!nextAnchorKey;

    if (hasPrev && hasNext) {
      const L = getRank(prevAnchorKey);
      const R = getRank(nextAnchorKey);
      const need = window.BigInt(runKeys.length + 1);
      const span = R - L;

      if (span > need) {
        const step = span / need;
        let r = L;
        for (const k of runKeys) {
          r += step;
          updates[k] = toStr(r);
        }
        return;
      }
      // Not enough room between anchors → signal to renumber whole group
      throw new Error('TIGHT_SPAN');
    }

    if (hasPrev && !hasNext) {
      // Tail run: continue with fixed step at the end
      let r = getRank(prevAnchorKey);
      for (const k of runKeys) {
        r += BASE_STEP;
        updates[k] = toStr(r);
      }
      return;
    }

    if (!hasPrev && hasNext) {
      // Head run: assign before the first anchor (descending), keeping order
      let r = getRank(nextAnchorKey);
      // assign backwards so earlier items get smaller ranks
      for (let i = runKeys.length - 1; i >= 0; i--) {
        r -= BASE_STEP;
        updates[runKeys[i]] = toStr(r);
      }
      return;
    }

    // No anchors at either side shouldn't happen here
    for (const k of runKeys) updates[k] = toStr(START); // fallback
  };

  let i = 0;
  let prevAnchor = null;

  while (i < defaultOrderedKeys.length) {
    // collect a run of missing keys until we hit the next anchor (or end)
    const run = [];
    while (i < defaultOrderedKeys.length && !haveRank(defaultOrderedKeys[i])) {
      run.push(defaultOrderedKeys[i]);
      i++;
    }

    const nextAnchor = i < defaultOrderedKeys.length ? defaultOrderedKeys[i] : null;

    if (run.length) {
      try {
        assignRun(run, prevAnchor, nextAnchor);
      } catch (e) {
        if (e.message === 'TIGHT_SPAN') {
          // Give up on precision; renumber the whole group using current order
          const groupKeys = [...defaultOrderedKeys];
          return { ...ranksMap, ...renumberGroup(groupKeys) };
        }
        throw e;
      }
    }

    // advance past the anchor
    if (nextAnchor) {
      prevAnchor = nextAnchor;
      i++;
    }
  }

  return { ...ranksMap, ...updates };
}

export default function useIssueOrdering({
  viewKey,                 // e.g. "my", "others", "board"
  issues,                  // array of issue objects in *default* order from API
  initialRanks,            // { [issueKey]: "bigint string" } from server
  pinnedSet,               // Set<string> of issue keys that are pinned
  groupBy,                 // (issue) => string  (e.g., 'pinned'|'unpinned' or 'active'|'done')
  saveRanks,               // async (updates: { [issueKey]: string }) => Promise
}) {
  const init = useRef(false);
  const [ranks, setRanks] = useState(() => ({ ...(initialRanks || {}) }));
  const initialRanksLoaded = useRef(false);
  const lastViewKey = useRef(viewKey);
  const hasHydrated = useRef(false);

  // Reset when view changes
  useEffect(() => {
    if (viewKey !== lastViewKey.current) {
      initialRanksLoaded.current = false;
      lastViewKey.current = viewKey;
      hasHydrated.current = false;
    }
  }, [viewKey]);

  // Update ranks when initialRanks changes (e.g., after fetching from server)
  useEffect(() => {
    if (initialRanks === undefined) return;

    const rankCount = Object.keys(initialRanks || {}).length;
    console.log('[useIssueOrdering] Loading initial ranks:', rankCount, 'items');

    // Update ranks with loaded data
    setRanks(initialRanks || {});

    // Always mark as loaded when initialRanks is provided, even if empty
    // This allows the hydration effect to run and auto-generate ranks
    initialRanksLoaded.current = true;
  }, [initialRanks]);

  // Default group function: just separate pinned vs unpinned
  const groupFn = groupBy || ((issue) => (pinnedSet?.has(issue.key) ? 'pinned' : 'unpinned'));

  // Per group list of keys in *default* order (from API)
  const groups = useMemo(() => {
    const map = new Map();
    for (const issue of issues) {
      const g = groupFn(issue);
      if (!map.has(g)) map.set(g, []);
      map.get(g).push(issue.key);
    }
    return map; // Map<group, string[]>
  }, [issues, groupFn]);

  // hasHydrated is declared above with other refs

  // Hydrate any *missing* ranks when issues change (new arrivals)
  useEffect(() => {
    const effectStart = performance.now();

    // Skip if we haven't loaded initial ranks yet
    if (!initialRanksLoaded.current) {
      return;
    }

    const nextRanks = { ...ranks };
    let changed = false;

    for (const [group, keys] of groups) {
      // Only fill ranks for issues that don't have any rank at all
      const missingRankKeys = keys.filter(k => nextRanks[k] == null);

      if (missingRankKeys.length === 0) continue; // All have ranks, skip

      // Get existing ranks in this group
      const existingRankedKeys = keys.filter(k => nextRanks[k] != null);

      if (existingRankedKeys.length > 0) {
        // We have some ranked items - add new ones at the end
        // Find the maximum rank in this group
        let maxRank = 0n;
        for (const k of existingRankedKeys) {
          const r = toBig(nextRanks[k]);
          if (r > maxRank) maxRank = r;
        }

        // Add missing items after the max rank
        let r = maxRank + BASE_STEP;
        for (const k of missingRankKeys) {
          nextRanks[k] = toStr(r);
          r += BASE_STEP;
          changed = true;
        }
      } else {
        // No ranks at all in this group - assign default ranks
        // This only happens for completely new groups or first time setup
        let r = START;
        for (const k of keys) {
          nextRanks[k] = toStr(r);
          r += BASE_STEP;
          changed = true;
        }
      }
    }

    if (changed) {
      setRanks(nextRanks);

      // DO NOT auto-save ranks for new issues - only save when user manually reorders
      // This prevents storing ranks for all 1000+ issues in the cache
      // Ranks will be auto-generated in memory for display purposes, but not persisted
      const effectDuration = performance.now() - effectStart;
      console.log('[useIssueOrdering] Auto-generated ranks for new issues (not saving to server)', { duration: `${effectDuration.toFixed(0)}ms`, totalRanks: Object.keys(nextRanks).length });
    }

    hasHydrated.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups, saveRanks]); // re-run when default-ordered groups change

  // Cache BigInt conversions to avoid repeated parsing
  const ranksCache = useRef(new Map());

  // Return issues ordered by group, then by numeric rank
  const orderedIssues = useMemo(() => {
    const memoStart = performance.now();

    // Clear cache if ranks object changed
    if (ranksCache.current.size > 1000) {
      ranksCache.current.clear();
    }

    // group → list of issues sorted by rank
    const byGroup = new Map();
    for (const [group, keys] of groups) {
      // Convert ranks with caching
      const withRanks = [];
      for (const k of keys) {
        const rankStr = ranks[k];
        if (rankStr != null) {
          let bigRank = ranksCache.current.get(rankStr);
          if (!bigRank) {
            bigRank = toBig(rankStr);
            ranksCache.current.set(rankStr, bigRank);
          }
          withRanks.push({ key: k, rank: bigRank });
        } else {
          withRanks.push({ key: k, rank: null });
        }
      }

      // Sort by cached BigInt values
      withRanks.sort((a, b) => {
        if (a.rank == null && b.rank == null) return 0;
        if (a.rank == null) return 1;
        if (b.rank == null) return -1;
        return a.rank < b.rank ? -1 : a.rank > b.rank ? 1 : 0;
      });

      byGroup.set(group, withRanks.map(x => x.key));
    }

    // Concatenate groups in a deterministic order
    const groupNames = Array.from(byGroup.keys()).sort((a, b) => {
      // Prioritize pinned groups
      const aPinned = a.startsWith('pinned');
      const bPinned = b.startsWith('pinned');
      if (aPinned && !bPinned) return -1;
      if (!aPinned && bPinned) return 1;

      // Then by active vs done
      const aActive = a.includes('active');
      const bActive = b.includes('active');
      if (aActive && !bActive) return -1;
      if (!aActive && bActive) return 1;

      return 0;
    });

    const keyToIssue = new Map(issues.map(i => [i.key, i]));
    const finalKeys = groupNames.flatMap(g => byGroup.get(g) || []);
    const result = finalKeys.map(k => keyToIssue.get(k)).filter(Boolean);

    const memoDuration = performance.now() - memoStart;
    if (memoDuration > 10) {
      console.log('[useIssueOrdering] orderedIssues memo:', { duration: `${memoDuration.toFixed(0)}ms`, issueCount: result.length });
    }

    return result;
  }, [groups, ranks, issues]);

  /** Public: call from your DnD onDragEnd handler */
  const moveIssue = useCallback(async (issueKey, prevKey, nextKey) => {
    // Determine the issue's group and use neighbors inside that group only
    const issue = issues.find(i => i.key === issueKey);
    if (!issue) return;

    const g = groupFn(issue);
    const groupKeys = groups.get(g) || [];

    // Compute rank from provided neighbor keys (can be null at ends)
    const prevRank = prevKey ? ranks[prevKey] : null;
    const nextRank = nextKey ? ranks[nextKey] : null;

    let newRank = between(prevRank, nextRank);
    let updates = {};

    // If no space between neighbors, renumber that whole group and recompute
    if (!newRank) {
      const renum = renumberGroup(groupKeys);
      updates = { ...renum };
      const p = prevKey ? renum[prevKey] : null;
      const n = nextKey ? renum[nextKey] : null;
      newRank = between(p, n) || renum[issueKey] || toStr(START);
    } else {
      updates = { [issueKey]: newRank };
    }

    // Update state and get the new complete ranks
    const newCompleteRanks = { ...ranks, ...updates };
    setRanks(newCompleteRanks);
    console.log('[useIssueOrdering] After move:', { issueKey, newRank, totalRanks: Object.keys(newCompleteRanks).length });

    // Save ALL ranks to backend to ensure consistency
    // This prevents issues with partial ranks on reload
    console.log('[useIssueOrdering] Saving ALL ranks to backend:', Object.keys(newCompleteRanks).length, 'items');
    saveRanks?.(newCompleteRanks).catch((err) => {
      console.error('[useIssueOrdering] Failed to save ranks:', err);
    });
  }, [groups, groupFn, issues, ranks, saveRanks]);

  return { orderedIssues, ranks, moveIssue };
}